/* eslint-disable no-console */
import { getLogger } from '../commons/logger';
import type { IReporter } from './IReporter';
import type { RunnerOptions } from '../runOptions';
import type { ExecutionList } from '../runners/TestPlanRunner';
import type { TestRunStatusTestResult } from '../testRunStatus';

const logger = getLogger('team-city-reporter');

function tidy(text: string | null | undefined) {
    if (!text) {
        return '';
    }
    return text
        .replace(/\|/g, '||')
        .replace(/'/g, "|'")
        .replace(/\n/g, '|n')
        .replace(/\r/g, '|r')
        .replace(/\u0085/g, '|x')
        .replace(/\u2028/g, '|l')
        .replace(/\u2029/g, '|p')
        .replace(/\[/g, '|[')
        .replace(/\]/g, '|]');
}

class TeamCityReporter implements IReporter {
    constructor(private readonly options: RunnerOptions) {
    }

    private getPrintName(test: TestRunStatusTestResult) {
        const testConfig = test.config || {};
        const testDataNumber = typeof testConfig.testDataTotal === 'number' ? ` - ${testConfig.testDataIndex} / ${testConfig.testDataTotal} Data set` : '';
        const teamCityName = `${test.name} (${test.testId})${testDataNumber}`;
        return tidy(teamCityName);
    }

    public onTestStarted(test: TestRunStatusTestResult, workerId: number, isRerun?: boolean, resultId?: string) {
        if (isRerun) {
            logger.debug('skip report test started because is rerun');
            return;
        }
        const name = this.getPrintName(test);
        console.log(`##teamcity[testStarted name='${name}' captureStandardOutput='true' flowId='${resultId}']`);
    }

    public onTestFailed(test: TestRunStatusTestResult, failureReason: TestRunStatusTestResult['failureReason'], testurl: string, testId: string, isRerun: boolean, resultId: string) {
        if (isRerun) {
            logger.debug('skip report test failed because is rerun');
            return;
        }
        const name = this.getPrintName(test);
        console.log(`##teamcity[testFailed name='${name}' message='${tidy(failureReason)}' details='${tidy(testurl)}' flowId='${resultId}']`);
    }

    public onTestFinished(test: TestRunStatusTestResult, workerId: number, isRerun?: boolean) {
        if (isRerun) {
            logger.debug('skip report test finished because is rerun');
            return;
        }
        const name = this.getPrintName(test);
        console.log(`##teamcity[testFinished name='${name}' duration='${test.duration}' flowId='${test.resultId}']`);
    }

    public onTestIgnored(workerId: number, test: TestRunStatusTestResult, message = 'ignore') {
        const name = this.getPrintName(test);
        console.log(`##teamcity[testIgnored name='${name}' message='${tidy(message)}']`);
    }

    onTestPlanStarted(beforeTests: ExecutionList, tests: ExecutionList, afterTests: ExecutionList, testPlanName: string) {
        console.log(`##teamcity[testSuiteStarted name='${tidy(testPlanName)}']`);
    }

    onTestPlanFinished(testResults: { [resultId: string]: TestRunStatusTestResult }, testPlanName: string) {
        console.log(`##teamcity[testSuiteFinished name='${tidy(testPlanName)}']`);
    }
}

export const Reporter = TeamCityReporter;
